/**
 * Vault Search Module
 * Search box rendering, scope toggle, and results filtering
 */

import * as State from './state.js';
import { escapeAttr } from '../../../../engine/core/validation.js';
import { t } from '../../../../shared/i18n/index.js';

// ============================================
// Dependencies (injected via init)
// ============================================

let _deps = {
  escapeHtml: null,
  renderMapItem: null,
  getFolderPathString: null,
};

/**
 * Initialize search module with dependencies
 * @param {Object} deps - Dependencies
 * @param {Function} deps.escapeHtml - HTML escape function
 * @param {Function} deps.renderMapItem - Map item renderer
 * @param {Function} deps.getFolderPathString - Folder path string getter
 */
export function initSearch(deps) {
  if (deps.escapeHtml) _deps.escapeHtml = deps.escapeHtml;
  if (deps.renderMapItem) _deps.renderMapItem = deps.renderMapItem;
  if (deps.getFolderPathString) _deps.getFolderPathString = deps.getFolderPathString;
}

// ============================================
// Search Box
// ============================================

/**
 * Update the search box UI to reflect current state
 */
export function updateSearchBox() {
  const searchBox = document.getElementById("vault-search-box");
  if (searchBox) {
    const input = searchBox.querySelector(".vault-search-input");
    const searchQuery = State.getSearchQuery();
    if (input && input.value !== searchQuery) {
      input.value = searchQuery;
    }
    searchBox.classList.toggle("has-query", searchQuery.length > 0);
  }
}

// ============================================
// Search Scope Toggle
// ============================================

/**
 * Render the search scope toggle buttons
 * @returns {string} HTML string
 */
export function renderSearchScopeToggle() {
  const searchScope = State.getSearchScope();
  return `
    <div class="vault-search-scope">
      <button class="vault-scope-btn ${searchScope === "all" ? "active" : ""}" data-scope="all" title="${escapeAttr(t('vault.search.allTitle'))}">${_deps.escapeHtml(t('common.all'))}</button>
      <button class="vault-scope-btn ${searchScope === "folder" ? "active" : ""}" data-scope="folder" title="${escapeAttr(t('vault.search.folderTitle'))}">${_deps.escapeHtml(t('common.folder'))}</button>
    </div>
  `;
}

// ============================================
// Search Results Rendering
// ============================================

/**
 * Render search results based on current query
 * @param {Object} index - Vault index
 * @param {string} currentDocId - Currently open document ID
 * @returns {string} HTML string
 */
export function renderSearchResults(index, currentDocId) {
  const searchQuery = State.getSearchQuery();
  if (!searchQuery) return "";

  const query = searchQuery.toLowerCase();
  const searchScope = State.getSearchScope();
  const selectedFolderId = State.getSelectedFolderId();
  const VaultStorage = State.getVaultStorage();

  // Get maps to search based on scope (use docs[] if available, else maps[])
  const allOwnerDocs = (index.docs || []).filter(d => d.role === "owner");
  let mapsToSearch = allOwnerDocs;
  let foldersToSearch = index.folders;

  if (searchScope === "folder" && selectedFolderId) {
    // Search only current folder (not category folders)
    if (selectedFolderId !== State.CATEGORY_SHARED) {
      mapsToSearch = allOwnerDocs.filter((m) => m.folderId === selectedFolderId);
      foldersToSearch = index.folders.filter(
        (f) => f.parentId === selectedFolderId,
      );
    }
  }

  const matchedFolders = foldersToSearch.filter((f) =>
    f.name.toLowerCase().includes(query),
  );
  const matchedMaps = mapsToSearch.filter((m) =>
    m.title.toLowerCase().includes(query),
  );

  // Also search shared items if searching all
  let matchedShared = [];

  if (searchScope === "all") {
    const index = VaultStorage.getIndex();
    const sharedDocs = (index?.docs || []).filter(d => d.role !== "owner");
    const ownedSharesCache = State.getOwnedSharesCache();
    const matchedReceived = sharedDocs
      .filter((s) => (s.title || "").toLowerCase().includes(query))
      .map((s) => ({ type: "received", shareId: s.docId, inviterUsername: s.ownerUsername, ...s }));
    const matchedOwned = ownedSharesCache
      .filter((s) => (s.title || "").toLowerCase().includes(query))
      .map((s) => ({ type: "owned", ...s }));
    matchedShared = [...matchedReceived, ...matchedOwned];
  }

  const totalResults =
    matchedFolders.length + matchedMaps.length + matchedShared.length;

  if (totalResults === 0) {
    const key = searchScope === "folder" ? 'vault.search.noResultsInFolder' : 'vault.search.noResults';
    return `<div class="vault-empty-folder">${_deps.escapeHtml(t(key, { query: searchQuery }))}</div>`;
  }

  let html = "";

  if (matchedFolders.length > 0) {
    html += `<div class="vault-search-section-title">${_deps.escapeHtml(t('vault.nav.folders'))}</div>`;
    html += matchedFolders
      .map((folder) => {
        const path = _deps.getFolderPathString(folder.id, index);
        return `
          <div class="vault-search-folder" data-folder-id="${escapeAttr(folder.id)}">
            <div class="vault-map-icon">📁</div>
            <div class="vault-map-info">
              <div class="vault-map-title">${_deps.escapeHtml(folder.name)}</div>
              <div class="vault-map-path">${_deps.escapeHtml(path)}</div>
            </div>
          </div>
        `;
      })
      .join("");
  }

  if (matchedMaps.length > 0) {
    html += `<div class="vault-search-section-title">${_deps.escapeHtml(t('vault.myMaps'))}</div>`;
    html += matchedMaps
      .map((map) => _deps.renderMapItem(map, currentDocId, false, true))
      .join("");
  }

  if (matchedShared.length > 0) {
    html += `<div class="vault-search-section-title">${_deps.escapeHtml(t('vault.category.shared'))}</div>`;
    html += matchedShared
      .map((share) => {
        if (share.type === "received") {
          return `
            <div class="vault-search-share" data-share-id="${escapeAttr(share.shareId)}">
              <div class="vault-map-icon">📥</div>
              <div class="vault-map-info">
                <div class="vault-map-title">${_deps.escapeHtml(share.title)}</div>
                <div class="vault-map-path">${_deps.escapeHtml(t('vault.share.from', { name: share.inviterUsername || t('vault.share.unknownUser') }))}</div>
              </div>
            </div>
          `;
        } else {
          return `
            <div class="vault-search-owned-share" data-share-id="${escapeAttr(share.id)}">
              <div class="vault-map-icon">📤</div>
              <div class="vault-map-info">
                <div class="vault-map-title">${_deps.escapeHtml(share.title)}</div>
                <div class="vault-map-path">${_deps.escapeHtml(t('vault.map.sharedByYou'))}</div>
              </div>
            </div>
          `;
        }
      })
      .join("");
  }

  return html;
}
